<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import moment from 'moment'
import BaseIcon from '@/components/base/baseIcon.vue'

import { iconNames } from '@/enums/iconNames'
import { moduleName } from '@/enums/moduleNames'
import { pageNames } from '@/enums/pageNames'
import { userPermissionStore } from '@/stores/user/permission'
import { userProfileStore } from '@/stores/user/userProfile'
import { communityResearchStore } from '@/stores/communityResearch/communityResearch'

const route = useRoute()
const router = useRouter()
const communityResearch = communityResearchStore()

enum mainMenu {
  NONE = 'NONE',
  USER = 'USER',
  COMMUNITY = 'COMMUNITY',
  RESEARCH = 'RESEARCH',
  NEWS = 'NEWS',
  INSTITUTION = 'INSTITUTION',
  SUBSCRIPTION = 'SUBSCRIPTION',
  TOOLS = 'TOOLS',
  ADBANNER = 'ADBANNER'
}

interface menuItem {
  pageName: pageNames | null
  pageLabel: string
  module: moduleName | moduleName[] | null
  icon: iconNames | null
  tag: mainMenu | null
  submenu: menuItem[]
  subMenuActive: boolean
  pageGroup: pageNames[]
}
const menuSets = ref<menuItem[]>([
  {
    pageName: pageNames.HOME,
    module: null,
    pageLabel: 'Dashboard',
    icon: iconNames.MENUICON,
    tag: mainMenu.NONE,
    submenu: [],
    subMenuActive: false,
    pageGroup: [pageNames.HOME]
  },
  {
    pageName: pageNames.USER,
    module: moduleName.INTERNAL_USER,
    pageLabel: 'Internal User',
    icon: iconNames.INTERNALUSER,
    tag: mainMenu.NONE,
    submenu: [],
    subMenuActive: false,
    pageGroup: [
      pageNames.USER,
      pageNames.USEREDITOREXISTING,
      pageNames.USEREDITORNEW,
      pageNames.PERMISSION,
      pageNames.PERMISSIONEDITOREXISTING,
      pageNames.PERMISSIONEDITORNEW
    ]
  },
  {
    pageName: null,
    module: null,
    pageLabel: 'Circle',
    icon: iconNames.COMMUNITY,
    tag: mainMenu.COMMUNITY,
    submenu: [
      {
        pageName: pageNames.COMMUNITYRESEARCHCONTENT,
        module: moduleName.CIRCLE_RESEARCH,
        pageLabel: 'Content',
        icon: null,
        tag: mainMenu.COMMUNITY,
        submenu: [],
        subMenuActive: false,
        pageGroup: [pageNames.COMMUNITYRESEARCHCONTENT, pageNames.COMMUNITYRESEARCHEDITOREXISTING]
      },
      {
        pageName: pageNames.CONTRIBUTOR,
        module: moduleName.CONTENT_CONTRIBUTOR,
        pageLabel: 'Contributor',
        icon: null,
        tag: mainMenu.COMMUNITY,
        submenu: [],
        subMenuActive: false,
        pageGroup: [
          pageNames.CONTRIBUTOR,
          pageNames.CONTRIBUTOREDITOREXISTING,
          pageNames.CONTRIBUTOREDITORNEW
        ]
      }
    ],
    subMenuActive: false,
    pageGroup: [
      pageNames.COMMUNITYRESEARCHCONTENT,
      pageNames.COMMUNITYRESEARCHEDITOREXISTING,
      pageNames.CONTRIBUTOR,
      pageNames.CONTRIBUTOREDITOREXISTING,
      pageNames.CONTRIBUTOREDITORNEW,
      pageNames.CONTRIBUTOREDITORNEW
    ]
  },
  {
    pageName: null,
    module: null,
    pageLabel: 'Institution',
    icon: iconNames.INSTITUTION,
    tag: mainMenu.INSTITUTION,
    submenu: [
      {
        pageName: pageNames.INSTITUTIONCONTENT,
        module: moduleName.INSTITUTION_CONTENT,
        pageLabel: 'Content',
        icon: null,
        tag: mainMenu.INSTITUTION,
        submenu: [],
        subMenuActive: false,
        pageGroup: [
          pageNames.INSTITUTIONCONTENT,
          pageNames.INSTITUTIONCONTENTEDITOREXISTING,
          pageNames.INSTITUTIONCONTENTEDITORNEW
        ]
      },
      {
        pageName: pageNames.INSTITUTION,
        module: moduleName.INSTITUTION,
        pageLabel: 'Broker Data',
        icon: null,
        tag: mainMenu.INSTITUTION,
        submenu: [],
        subMenuActive: false,
        pageGroup: [pageNames.INSTITUTION, pageNames.INSTITUTIONEDITORNEW, pageNames.INSTITUTIONEDITOREXISTING]
      },
      {
        pageName: pageNames.INSTITUTIONSTOCKPLACEMENT,
        module: moduleName.INSTITUTION_STOCK_PLACEMENT,
        pageLabel: 'Selection Stocks',
        icon: null,
        tag: mainMenu.INSTITUTION,
        submenu: [],
        subMenuActive: false,
        pageGroup: [
          pageNames.INSTITUTIONSTOCKPLACEMENT,
          pageNames.INSTITUTIONSTOCKPLACEMENTDETAIL,
        ]
      }
    ],
    subMenuActive: false,
    pageGroup: [
      pageNames.INSTITUTION,
      pageNames.INSTITUTIONEDITORNEW,
      pageNames.INSTITUTIONEDITOREXISTING,
      pageNames.INSTITUTIONCONTENT,
      pageNames.INSTITUTIONCONTENTEDITOREXISTING,
      pageNames.INSTITUTIONCONTENTEDITORNEW,
      pageNames.INSTITUTIONSTOCKPLACEMENT,
      pageNames.INSTITUTIONSTOCKPLACEMENTDETAIL,
    ]
  },
  {
    pageName: pageNames.STREETCALLCONTENT,
    module: moduleName.STREETCALL_CONTENT,
    pageLabel: 'Streetcall',
    icon: iconNames.INSTITUTION,
    tag: mainMenu.NONE,
    submenu: [],
    subMenuActive: false,
    pageGroup: [
      pageNames.STREETCALLCONTENT,
      pageNames.STREETCALLCONTENTEDITOREXISTING,
      pageNames.STREETCALLCONTENTEDITORNEW
    ]
  },
  {
    pageName: null,
    module: null,
    pageLabel: 'Exclusive',
    icon: iconNames.RESEARCH,
    tag: mainMenu.RESEARCH,
    submenu: [
      {
        pageName: pageNames.ALGORESEARCH,
        module: moduleName.ALGO_RESEARCH,
        pageLabel: 'Content',
        icon: null,
        tag: mainMenu.RESEARCH,
        submenu: [],
        subMenuActive: false,
        pageGroup: [
          pageNames.ALGORESEARCH,
          pageNames.ALGORESEARCHEDITOREXISTING,
          pageNames.ALGORESEARCHEDITORNEW
        ]
      },
      {
        pageName: pageNames.RESEARCHBLAST,
        module: moduleName.ALGO_RESEARCH_BLAST,
        pageLabel: 'Research Blast',
        icon: null,
        tag: mainMenu.RESEARCH,
        submenu: [],
        subMenuActive: false,
        pageGroup: [
          pageNames.RESEARCHBLAST,
          pageNames.RESEARCHBLASTNEW,
          pageNames.RESEARCHBLASTEDITOREXISTING
        ]
      },
    ],
    subMenuActive: false,
    pageGroup: [
      pageNames.ALGORESEARCH,
      pageNames.ALGORESEARCHEDITOREXISTING,
      pageNames.RESEARCHBLAST,
      pageNames.RESEARCHBLASTNEW,
      pageNames.RESEARCHBLASTEDITOREXISTING,
      pageNames.ALGORESEARCHEDITORNEW,
      // pageNames.NEWSLETTER,
      // pageNames.NEWSLETTEREDITORUPDATE,
      // pageNames.NEWSLETTERDETAILEXISTING,
      // pageNames.CATEGORY,
      // pageNames.CATEGORYEDITORNEW,
      // pageNames.CATEGORYEDITOREXISTING,
      // pageNames.MASTERTAG,
      // pageNames.MASTERTAGNEW
    ]
  },
  {
    pageName: pageNames.ALGONEWS,
    module: moduleName.ALGO_NEWS,
    pageLabel: 'Insight',
    icon: iconNames.NEWS,
    tag: mainMenu.NONE,
    submenu: [],
    subMenuActive: false,
    pageGroup: [
      pageNames.ALGONEWS,
      pageNames.ALGONEWSEDITOREXISTING,
      pageNames.ALGONEWSEDITORNEW
    ]
  },
  {
    pageName: pageNames.CLIENT,
    module: moduleName.CLIENT,
    pageLabel: 'Client',
    icon: iconNames.CLIENT,
    tag: mainMenu.NONE,
    submenu: [],
    subMenuActive: false,
    pageGroup: [pageNames.CLIENT]
  },
  {
    pageName: null,
    module: null,
    pageLabel: 'Subscriptions',
    icon: iconNames.SUBSCRIPTION,
    tag: mainMenu.SUBSCRIPTION,
    submenu: [
      {
        pageName: pageNames.SUBSCRIPTION,
        module: [moduleName.SUBSCRIPTION],
        pageLabel: 'Master Data',
        icon: null,
        tag: mainMenu.SUBSCRIPTION,
        submenu: [],
        subMenuActive: false,
        pageGroup: [
          pageNames.SUBSCRIPTION,
          pageNames.SUBSCRIPTIONCREATE
        ]
      }
    ],
    subMenuActive: false,
    pageGroup: [
      pageNames.SUBSCRIPTION, 
      pageNames.SUBSCRIPTIONCREATE, pageNames.SUBSCRIPTIONDETAIL]
  },
  {
    pageName: null,
    module: null,
    pageLabel: 'Tools',
    icon: iconNames.TOOL,
    tag: mainMenu.TOOLS,
    submenu: [
      {
        pageName: pageNames.STOCKS,
        module: [
          moduleName.INDEX_EDITOR,
          moduleName.STOCK_PLACEMENT,
          moduleName.MASTER_STOCK_INDEX
        ],
        pageLabel: 'Stocks',
        icon: null,
        tag: mainMenu.TOOLS,
        submenu: [],
        subMenuActive: false,
        pageGroup: [pageNames.STOCKS]
      },
      {
        pageName: pageNames.HYBRIDSLIDER,
        module: moduleName.HYBRID_SLIDER,
        pageLabel: 'Content Placement',
        icon: null,
        tag: mainMenu.TOOLS,
        submenu: [],
        subMenuActive: false,
        pageGroup: [
          pageNames.HYBRIDSLIDER,
          pageNames.HYBRIDSLIDERCREATE,
          pageNames.HYBRIDSLIDERDETAIL
        ]
      },
      {
        pageName: pageNames.TEXTSLIDER,
        module: moduleName.TEXT_SLIDER,
        pageLabel: 'Text Slider',
        icon: null,
        tag: mainMenu.TOOLS,
        submenu: [],
        subMenuActive: false,
        pageGroup: [pageNames.TEXTSLIDER, pageNames.TEXTSLIDERCREATE, pageNames.TEXTSLIDERDETAIL]
      },
      {
        pageName: pageNames.NEWSLETTER,
        module: moduleName.NEWS_LETTER,
        pageLabel: 'Newsletter',
        icon: null,
        tag: mainMenu.TOOLS,
        submenu: [],
        subMenuActive: false,
        pageGroup: [
          pageNames.NEWSLETTER,
          pageNames.NEWSLETTEREDITORUPDATE,
          pageNames.NEWSLETTERDETAILEXISTING
        ]
      },
      {
        pageName: pageNames.CATEGORY,
        module: moduleName.RESEARCH_CATEGORY,
        pageLabel: 'Category',
        icon: null,
        tag: mainMenu.TOOLS,
        submenu: [],
        subMenuActive: false,
        pageGroup: [
          pageNames.CATEGORY,
          pageNames.CATEGORYEDITORNEW,
          pageNames.CATEGORYEDITOREXISTING
        ]
      },
      {
        pageName: pageNames.MASTERTAG,
        module: moduleName.MASTER_RESEARCH_TAG,
        pageLabel: 'Master Tag',
        icon: null,
        tag: mainMenu.TOOLS,
        submenu: [],
        subMenuActive: false,
        pageGroup: [pageNames.MASTERTAG, pageNames.MASTERTAGNEW]
      },
      {
        pageName: pageNames.ACCOUNT_WALL,
        module: moduleName.ACCOUNT_WALL,
        pageLabel: 'Account Wall',
        icon: null,
        tag: mainMenu.TOOLS,
        submenu: [],
        subMenuActive: false,
        pageGroup: [pageNames.ACCOUNT_WALL]
      },
      {
        pageName: pageNames.LEGAL,
        module: [moduleName.CIRCLE_DISCLOSURE, moduleName.LEGAL_INFORMATION],
        pageLabel: 'Legal',
        icon: null,
        tag: mainMenu.TOOLS,
        submenu: [],
        subMenuActive: false,
        pageGroup: [pageNames.LEGAL, pageNames.LEGALCIRCLEDISCLOSURE, pageNames.LEGALCIRCLEDISCLOSURENEW, pageNames.LEGALINFORMATION, pageNames.LEGALINFORMATIONEDITOR]
      }
    ],
    subMenuActive: false,
    pageGroup: [
      pageNames.HYBRIDSLIDER,
      pageNames.HYBRIDSLIDERCREATE,
      pageNames.HYBRIDSLIDERDETAIL,
      pageNames.STOCKS,
      pageNames.TEXTSLIDER,
      pageNames.TEXTSLIDERCREATE,
      pageNames.TEXTSLIDERDETAIL,
      pageNames.NEWSLETTER,
      pageNames.NEWSLETTEREDITORUPDATE,
      pageNames.NEWSLETTERDETAILEXISTING,
      pageNames.CATEGORY,
      pageNames.CATEGORYEDITORNEW,
      pageNames.CATEGORYEDITOREXISTING,
      pageNames.MASTERTAG,
      pageNames.MASTERTAGNEW,
      pageNames.ACCOUNT_WALL,
      pageNames.LEGAL,
      pageNames.LEGALCIRCLEDISCLOSURE,
      pageNames.LEGALCIRCLEDISCLOSURENEW, 
      pageNames.LEGALINFORMATION,
      pageNames.LEGALINFORMATIONEDITOR
    ]
  },
  {
    pageName: null,
    module: null,
    pageLabel: 'Ad',
    icon: iconNames.ADS,
    tag: mainMenu.ADBANNER,
    submenu: [
      {
        pageName: pageNames.ADBANNER,
        module: moduleName.AD_BANNER,
        pageLabel: 'Master Ad',
        icon: null,
        tag: mainMenu.ADBANNER,
        submenu: [],
        subMenuActive: false,
        pageGroup: [pageNames.ADBANNER, pageNames.ADBANNEREDITOREXISTING, pageNames.ADBANNEREDITORNEW]
      },
    ],
    subMenuActive: false,
    pageGroup: [
      pageNames.ADBANNER,
      pageNames.ADBANNEREDITOREXISTING,
      pageNames.ADBANNEREDITORNEW,
    ]
  },
])

const shrinked = ref(false)
const currentDateTime = ref<moment.Moment>(moment())
const showUserMenu = ref(false)
const userProfile = userProfileStore()
const userPermission = userPermissionStore()
const currentRouteName = computed(() => route.name as pageNames)

function hideSubmenuExcept(page: mainMenu) {
  // subMenuUser.value = page === mainMenu.USER ? !subMenuUser.value : false
  // subMenuCommunity.value = page === mainMenu.COMMUNITY ? !subMenuCommunity.value : false
  // subMenuResearch.value = page === mainMenu.RESEARCH ? !subMenuResearch.value : false

  //new implementation
  menuSets.value.forEach((item) => {
    item.subMenuActive = item.tag === page ? !item.subMenuActive : false
  })
}

function hasPermission(permission: moduleName): boolean {
  return userPermission.getUserPermission.read_action.includes(permission)
}
function hasPermissions(permission: moduleName[]): boolean {
  if (permission.length === 0) {
    return true
  }

  for (var x = 0; x < permission.length; x++) {
    if (userPermission.getUserPermission.read_action.includes(permission[x])) {
      return true
    }
  }
  return false
}

function getModules(menu: menuItem[], parent: moduleName | null) {
  let modules: moduleName[] = []
  if (parent !== null) {
    modules.push(parent)
  }
  for (var x = 0; x < menu.length; x++) {
    if (menu[x].module !== null) {
      if (Array.isArray(menu[x].module!)) {
        modules = modules.concat(menu[x].module!)
      } else {
        modules.push(menu[x].module! as moduleName)
      }
    }
  }
  
  return modules
}

setInterval(() => {
  currentDateTime.value = moment()
}, 1000)
</script>
<template>
  <div class="md:hidden w-full h-full bg-[--algo-brand-hadfield-blue-800] relative">
    <div class="flex flex-col p-6 justify-between border-t">
      <div class="flex justify-start">
        <div class="flex grow px-1.5 2xl:px-5 py-2 border border-[--algo-base-white] rounded-lg">
          <div class="my-auto">
            <BaseIcon :iconName="iconNames.CALENDAR" :size="20" hex-color="#FEFEFE"></BaseIcon>
          </div>
          <div class="my-auto pr-0 pl-2 lg:pr-1.5 text-[--algo-base-white]">
            <p class="font-semibold text-xs">{{ currentDateTime.format('dddd') }},</p>
            <p class="font-normal text-ss">{{ currentDateTime.format('D MMMM YYYY - HH:mm') }}</p>
          </div>
        </div>
      </div>
      <div class="flex mt-[16px] h-[52px] text-[--algo-base-white]" @click="showUserMenu = !showUserMenu">
        <img :src="'/img/default-avatar.png'" class="rounded-xl mr-4 w-[52px]" />
        <div class="grow">
          <div class="text-[16px] font-semibold">
            {{ userProfile.getUserProfile.fullname || '' }}
          </div>
          <div class="text-[12px]">{{ userProfile.getUserProfile.email || '' }}</div>
        </div>
        <div class="my-auto">
          <BaseIcon :icon-name="showUserMenu ? iconNames.TOP : iconNames.BOT" />
        </div>
      </div>
      <div class="flex flex-col gap-[16px] mt-[16px] text-[--algo-base-white]" v-show="showUserMenu">
        <button
          class="flex flex-row items-center p-[8px] hover:bg-[--algo-brand-violet-100] rounded-[6px]"
          type="button"
          @click="() => router.push({ name: pageNames.USERPROFILE })"
        >
          <span class="grow text-left font-medium">My Account</span>
          <BaseIcon :icon-name="iconNames.INTERNALUSER" />
        </button>
        <button
          @click="router.push({ name: pageNames.LOGOUT })"
          class="flex flex-row items-center p-[8px] hover:bg-[--algo-brand-violet-100] rounded-[6px]"
        >
          <span class="grow text-left font-medium">Log Out</span>
          <BaseIcon :icon-name="iconNames.SIGNOUT" :size="24" svg-class="fill-white" />
        </button>
      </div>
    </div>
    <div class="flex border-t"></div>
    <!-- <div class="border-t-[1px] border-slate-300"></div> -->
    <div class="py-8 px-[20px] border-slate-300 justify-start">
      <p class="text-[--algo-base-white] font-semibold text-start text-sm">Main</p>
      <!-- AUTOMATED -->
      <div v-for="menu in menuSets" :key="menu.pageName!">
        <!-- <div
          class="absolute bg-white left-[128px] z-50 rounded-md w-[212px] overflow-x-hidden flex flex-col"
          :class="[shrinked && menu.subMenuActive ? 'border' : '']"
        >
          <Transition name="fade" v-for="submenu in menu.submenu" :key="submenu.pageName!">
            <div v-show="shrinked && menu.subMenuActive" class="flex">
              <button
                v-if="hasPermission(submenu.module!)"
                :class="[
                  shrinked ? 'flex' : 'hidden',
                  submenu.pageGroup.includes(currentRouteName!) ? 'active' : ''
                ]"
                class="justify-start items-center w-full p-2 text-menubar text-base font-medium menubar-list cursor-pointer"
                @click="() => router.push({ name: submenu.pageName! })"
              >
                <div
                  :class="[
                    submenu.pageGroup.includes(currentRouteName!)
                      ? 'menubar-submenu-active'
                      : 'menubar-submenu',
                    shrinked ? 'ml-0' : 'ml-4'
                  ]"
                  class="my-auto ml-4"
                  width="24px"
                  height="24px"
                ></div>
                <p class="pl-3 w-full text-left">{{ submenu.pageLabel }}</p>
              </button>
            </div>
          </Transition>
        </div> -->
        <!-- end of SHRINKED menu -->
        <button
          v-if="hasPermissions(getModules(menu.submenu, menu.module as moduleName))"
          :class="[
            shrinked ? 'justify-center' : 'justify-start',
            menu.pageGroup.includes(currentRouteName) ? 'active' : ''
          ]"
          @click="
            () => {
              menu.submenu.length > 0
                ? hideSubmenuExcept(menu.tag!)
                : router.push({ name: menu.pageName! })
            }
          "
          class="flex w-full mt-4 p-2 text-menubar text-base font-medium rounded-md cursor-pointer menubar-list group"
        >
          <div width="24px" height="24px">
            <BaseIcon
              :icon-name="menu.icon!"
              :svg-class="
                [
                  menu.pageGroup.includes(currentRouteName)
                    ? 'fill-white'
                    : 'fill-[--algo-accent-slate-500] md:group-hover:fill-white'
                ].join(' ')
              "
            />
          </div>

          <div class="flex w-full justify-between">
            <p :class="[shrinked ? 'hidden' : 'block']" class="pl-3">
              {{ menu.pageLabel }}
              <span 
                v-if="
                  menu.submenu.some(item => item.module === moduleName.CIRCLE_RESEARCH) && 
                  communityResearch.totalCircleRequest &&
                  (communityResearch.totalCircleRequest ?? 0) > 0
                " 
                class="px-[8px] py-[2px] ml-[12px] rounded-[100px] bg-[--algo-system-red-600] text-[12px] font-bold text-[--algo-base-white]"
              >
                {{ communityResearch.totalCircleRequest }}
              </span>
            </p>
            <div
              v-if="menu.submenu.length > 0"
              :class="[shrinked ? 'hidden' : 'block']"
              class="my-auto"
            >
              <BaseIcon
                v-if="!menu.subMenuActive"
                :icon-name="menu.subMenuActive ? iconNames.TOP : iconNames.BOT"
                :size="18"
                :svg-class="
                  menu.pageGroup.includes(currentRouteName)
                    ? 'fill-white'
                    : 'md:group-hover:fill-white'
                "
              />
            </div>
          </div>
        </button>
        <!-- SUBMENU SHRINKED FALSE -->
        <div v-for="submenu in menu.submenu" :key="submenu.pageName!">
          <Transition name="fade">
            <div v-show="menu.subMenuActive && !shrinked" class="justify-start block">
              <button
                v-if="
                  Array.isArray(submenu.module!)
                    ? hasPermissions(submenu.module!)
                    : hasPermission(submenu.module!)
                "
                :class="[submenu.pageGroup.includes(currentRouteName) ? 'active' : '']"
                class="flex w-full mt-2 p-2 text-menubar text-base font-medium rounded-md menubar-list cursor-pointer"
                @click="() => router.push({ name: submenu.pageName! })"
              >
                <div
                  :class="[
                    submenu.pageGroup.includes(currentRouteName)
                      ? 'menubar-submenu-active'
                      : 'menubar-submenu',
                    shrinked ? 'ml-0' : 'ml-4'
                  ]"
                  class="my-auto ml-4"
                  width="24px"
                  height="24px"
                ></div>
                <p :class="[shrinked ? 'hidden' : 'block']" class="pl-3">
                  {{ submenu.pageLabel }}
                </p>
              </button>
            </div>
          </Transition>
        </div>
      </div>
      <!-- end of AUTOMATED -->
    </div>
  </div>
</template>
